import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'

// BrainConstellation — a live, friendly map of the Marketing Brain and every source
// wired into it. The brain sits at the centre; connected sources orbit close in their
// own colour with data flowing inward, not-yet-connected ones sit further out, dim and
// dashed. Scroll to zoom, drag to pan, tap a source to see exactly what it feeds the
// brain, or tap the brain to talk to it.
//
// This is a faithful React port of the verified "Marketing Brain Constellation"
// prototype: the CSS is ported verbatim (scoped under .bc-root, class-prefixed .bc-*,
// keyframes bc-*), and the SVG/DOM is built imperatively in a useEffect against a ref
// using the prototype's own zoom/pan/hover/detail/chat logic. Only the mock data is
// swapped for the real API.
//
// DATA  GET /api/v1/marketing-intel/connectivity → nodes.
// CHAT  POST /api/v1/marketing-intel/ask { question } → { answer, sources, confidence }.
// If connectivity fails or is empty it falls back to representative sample nodes with a
// clearly-labelled "sample" ribbon (never fabricated numbers passed off as live).
//
// Backward compatible: still a default export; `highlight` (array of provider ids) lights
// ONLY those edges/nodes. MarketingBrain.jsx renders <BrainConstellation /> unchanged.
const API = '/api/v1/marketing-intel'

// ─── ported line icons (inline SVG paths) — do NOT add an external icon lib ──────────
const ICONS = {
  sparkles: '<path d="M9.9 15.5A2 2 0 0 0 8.5 14L2.4 12.5a.5.5 0 0 1 0-1L8.5 10A2 2 0 0 0 9.9 8.5l1.6-6.1a.5.5 0 0 1 1 0L14 8.5A2 2 0 0 0 15.5 10l6.1 1.5a.5.5 0 0 1 0 1L15.5 14a2 2 0 0 0-1.4 1.4l-1.6 6.1a.5.5 0 0 1-1 0z"/><path d="M20 3v4M22 5h-4"/>',
  search: '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
  'thumbs-up': '<path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>',
  'bar-chart-3': '<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
  globe: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  'shopping-bag': '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
  'credit-card': '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  'trending-up': '<path d="M22 7l-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/>',
  'at-sign': '<circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/>',
  music: '<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>',
  briefcase: '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  tag: '<path d="M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.42l9 9a2 2 0 0 0 2.82 0l7.17-7.17a2 2 0 0 0 0-2.82z"/><circle cx="7.5" cy="7.5" r="1.5"/>',
  cpu: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2"/>',
  send: '<path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/>',
}
const ic = (n) => `<svg viewBox="0 0 24 24">${ICONS[n] || ICONS.cpu}</svg>`

// ─── derive presentation for a real connector (icon / cadence / plain-language what) ──
// Real numbers come from the API; these maps only supply icon + cadence + a description
// of what the source DOES (no fabricated metrics).
const PROVIDER_ICON = {
  internal: 'sparkles', google_ads: 'search', meta_ads: 'thumbs-up', microsoft_ads: 'search',
  linkedin_ads: 'briefcase', tiktok_ads: 'music', amazon_ads: 'shopping-bag',
  google_analytics: 'bar-chart-3', ga4: 'bar-chart-3', google_tag_manager: 'tag', gtm: 'tag',
  mixpanel: 'bar-chart-3', amplitude: 'bar-chart-3', posthog: 'bar-chart-3', segment: 'tag',
  search_console: 'globe', bing_webmaster: 'globe', semrush: 'trending-up', ahrefs: 'trending-up',
  shopify: 'shopping-bag', stripe: 'credit-card', klaviyo: 'mail', mailchimp: 'at-sign',
  hubspot: 'users', salesforce: 'users',
}
const CAT_ICON = {
  Paid: 'search', Analytics: 'bar-chart-3', SEO: 'globe', Commerce: 'shopping-bag',
  CRM: 'users', Lifecycle: 'mail', Support: 'users', Calls: 'trending-up',
  Finance: 'credit-card', Product: 'tag',
}
const CAT_FREQ = {
  Paid: 'every 15 min', Analytics: 'hourly', SEO: 'daily', Commerce: 'every 5 min',
  CRM: 'hourly', Lifecycle: 'hourly', Support: 'live', Calls: 'live', Finance: 'live', Product: 'live',
}
const PROVIDER_FREQ = { internal: 'live', stripe: 'live', shopify: 'every 5 min' }
const CAT_ACTIVITY = {
  Paid: 'streaming ad spend, clicks and conversions per campaign so the Brain can watch cost-per-result move',
  Analytics: 'reporting sessions and on-site engagement so the Brain sees where visits convert or bounce',
  SEO: 'feeding organic clicks, impressions and the queries that earned them, page by page',
  Commerce: 'streaming orders, revenue and carts — the money end of every campaign',
  CRM: 'connecting leads to real pipeline and closed revenue, not just form fills',
  Lifecycle: 'adding email/SMS-driven revenue and flow performance to the picture',
  Support: 'surfacing the conversations and tickets that reveal friction after the sale',
  Calls: 'attributing inbound calls to the campaigns that actually drove them',
  Finance: 'confirming real captured revenue and refunds so attribution reconciles to actual money',
  Product: 'streaming product events and identities so behaviour ties back to spend',
}
const iconFor = (p) => PROVIDER_ICON[p.provider] || CAT_ICON[p.category] || 'cpu'
const freqFor = (p) => PROVIDER_FREQ[p.provider] || CAT_FREQ[p.category] || 'periodic'
const activityFor = (p) => (p.provider === 'internal'
  ? 'streaming your own funnel in real time — every order, lead and reply the moment it happens'
  : (CAT_ACTIVITY[p.category] || 'feeding its data into the Brain so answers stay grounded in real numbers'))

// provides is a "·"-separated string → chips
const sendsFrom = (provides) => String(provides || '').split('·').map((s) => s.trim()).filter(Boolean)

// Map a live connector row → a constellation node.
function toNode(c) {
  return {
    id: c.provider,
    label: c.label || c.provider,
    cat: c.category || '',
    c: c.color || '#96a0af',
    on: !!c.connected,
    metrics: Number(c.metrics) || 0,
    freq: c.connected ? freqFor(c) : '—',
    sends: sendsFrom(c.provides).length ? sendsFrom(c.provides) : ['data'],
    activity: activityFor(c),
    icon: iconFor(c),
    last_sync_at: c.last_sync_at || null,
    // connector health (present only for connected sources on newer connectivity payloads)
    health_status: c.health_status || null,
    coveragePct: (c.coveragePct == null ? null : Number(c.coveragePct)),
    gaps: Array.isArray(c.gaps) ? c.gaps.filter(Boolean) : [],
  }
}

// status → pill class + label. ok=green, stale/degraded=amber, error=red.
const HEALTH_PILL = {
  ok: ['ok', 'Healthy'], stale: ['warn', 'Stale'],
  degraded: ['warn', 'Degraded'], error: ['err', 'Error'],
}

// Representative sample constellation — used only when the API can't be reached / is
// empty, and always shown behind a "sample" ribbon so it's never mistaken for live data.
const SAMPLE_SOURCES = [
  { id: 'internal', label: 'ReRight', cat: 'First-party', icon: 'sparkles', c: '#8E7CC3', on: true, metrics: 1240, freq: 'live', sends: ['campaigns', 'orders', 'leads', 'conversations'], activity: 'streaming your own funnel in real time — every order, lead and reply the moment it happens' },
  { id: 'google_ads', label: 'Google Ads', cat: 'Paid search', icon: 'search', c: '#E24A3B', on: true, metrics: 820, freq: 'every 15 min', sends: ['spend', 'clicks', 'conversions', 'CPC'], activity: 'pulling spend, clicks and conversions per campaign so the Brain can see cost-per-result move' },
  { id: 'meta_ads', label: 'Meta Ads', cat: 'Paid social', icon: 'thumbs-up', c: '#4E9AC4', on: true, metrics: 610, freq: 'every 15 min', sends: ['spend', 'reach', 'results', 'ROAS'], activity: 'sending reach and results by ad set — how prospecting vs retargeting is performing' },
  { id: 'ga4', label: 'GA4', cat: 'Analytics', icon: 'bar-chart-3', c: '#2FB4A6', on: true, metrics: 940, freq: 'hourly', sends: ['sessions', 'engagement', 'conversions', 'landing pages'], activity: 'reporting sessions and on-site engagement so the Brain sees where visits convert or bounce' },
  { id: 'search_console', label: 'Search Console', cat: 'SEO', icon: 'globe', c: '#EAB43C', on: true, metrics: 530, freq: 'daily', sends: ['clicks', 'impressions', 'queries', 'pages'], activity: 'feeding organic clicks, impressions and the queries that earned them, page by page' },
  { id: 'shopify', label: 'Shopify', cat: 'Commerce', icon: 'shopping-bag', c: '#4FAE5A', on: true, metrics: 710, freq: 'every 5 min', sends: ['orders', 'revenue', 'products', 'carts'], activity: 'streaming orders, revenue and abandoned carts — the money end of every campaign' },
  { id: 'stripe', label: 'Stripe', cat: 'Payments', icon: 'credit-card', c: '#5B6BE8', on: true, metrics: 300, freq: 'live', sends: ['charges', 'refunds', 'MRR', 'disputes'], activity: 'confirming real captured revenue and refunds so attribution reconciles to actual money' },
  { id: 'klaviyo', label: 'Klaviyo', cat: 'Email', icon: 'mail', c: '#E86A9C', on: false, metrics: 0, freq: '—', sends: ['flows', 'opens', 'clicks', 'email revenue'], activity: 'would add email-driven revenue and flow performance to the picture' },
  { id: 'hubspot', label: 'HubSpot', cat: 'CRM', icon: 'users', c: '#F2933C', on: false, metrics: 0, freq: '—', sends: ['contacts', 'deals', 'pipeline stages', 'MQL→SQL'], activity: 'would connect leads to real pipeline and closed revenue, not just form fills' },
  { id: 'semrush', label: 'Semrush', cat: 'SEO tools', icon: 'trending-up', c: '#8FB63C', on: false, metrics: 0, freq: '—', sends: ['rankings', 'keyword gaps', 'backlinks'], activity: 'would layer competitive rankings and keyword gaps onto your organic data' },
  { id: 'mailchimp', label: 'Mailchimp', cat: 'Email', icon: 'at-sign', c: '#38B2C4', on: false, metrics: 0, freq: '—', sends: ['campaigns', 'opens', 'audience growth'], activity: 'would bring newsletter performance and list growth into the Brain' },
  { id: 'tiktok_ads', label: 'TikTok Ads', cat: 'Paid social', icon: 'music', c: '#C64B8B', on: false, metrics: 0, freq: '—', sends: ['spend', 'views', 'conversions'], activity: 'would add short-video ad spend and conversions to your paid mix' },
  { id: 'linkedin_ads', label: 'LinkedIn Ads', cat: 'Paid B2B', icon: 'briefcase', c: '#B667C9', on: false, metrics: 0, freq: '—', sends: ['spend', 'leads', 'job-title reach'], activity: 'would track B2B ad spend and lead-gen by seniority and industry' },
  { id: 'gtm', label: 'Tag Manager', cat: 'Tracking', icon: 'tag', c: '#7C8794', on: false, metrics: 0, freq: '—', sends: ['events', 'triggers', 'conversions'], activity: 'would verify your conversion events are firing so every number can be trusted' },
]

// Default chat starters — real questions; the answer now comes from the live /ask API.
const SUGGESTIONS = [
  'Why did revenue change this month?',
  'Where am I wasting spend?',
  'What should I test this week?',
]

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const relTime = (iso) => {
  if (!iso) return '—'
  const t = new Date(iso).getTime(); if (Number.isNaN(t)) return '—'
  const m = Math.max(0, Math.round((Date.now() - t) / 60000))
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}
const confWord = (pct) => (pct == null ? '' : pct >= 75 ? 'high' : pct >= 50 ? 'medium' : 'low')

// ─── ported prototype CSS, scoped under .bc-root, classes .bc-*, keyframes bc-* ──────
const CSS = `
.bc-root{
  --bc-bg:#FBFBF7; --bc-panel:#FFFFFF; --bc-panel2:#F7F7F1; --bc-line:#ECEBE3; --bc-ink:#2E3A45; --bc-mut:#7C8794; --bc-dim:#AEB6BD;
  --bc-hub:#4E9AC4; --bc-hubfill:#EAF4FA; --bc-grid:rgba(46,58,69,.045);
  --bc-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.08);
  --bc-shadow-lg:0 10px 24px rgba(46,58,69,.10),0 34px 66px rgba(46,58,69,.16);
  font-family:Nunito,system-ui,-apple-system,sans-serif;color:var(--bc-ink);-webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .bc-root{
  --bc-bg:#0E1116; --bc-panel:#171B22; --bc-panel2:#12161C; --bc-line:#262C36; --bc-ink:#E7ECF1; --bc-mut:#95A0AD; --bc-dim:#5C6672;
  --bc-hub:#5FB0DB; --bc-hubfill:#132A38; --bc-grid:rgba(255,255,255,.035);
  --bc-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --bc-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 36px 74px rgba(0,0,0,.6);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .bc-root{
  --bc-bg:#0E1116; --bc-panel:#171B22; --bc-panel2:#12161C; --bc-line:#262C36; --bc-ink:#E7ECF1; --bc-mut:#95A0AD; --bc-dim:#5C6672;
  --bc-hub:#5FB0DB; --bc-hubfill:#132A38; --bc-grid:rgba(255,255,255,.035);
  --bc-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --bc-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 36px 74px rgba(0,0,0,.6);
}}
.bc-root *{box-sizing:border-box}
.bc-head{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;margin:0 4px 8px;}
.bc-head h3{font-size:20px;font-weight:800;margin:0;letter-spacing:-.02em;color:var(--bc-ink);}
.bc-head p{margin:4px 0 0;color:var(--bc-mut);font-size:13.5px;font-weight:500;}
.bc-count{text-align:right;font-size:12.5px;color:var(--bc-mut);font-weight:600;}
.bc-count b{color:var(--bc-hub);font-size:24px;font-weight:800;display:block;line-height:1;}

.bc-board{position:relative;background:var(--bc-panel);border:1px solid var(--bc-line);border-radius:24px;
  box-shadow:var(--bc-shadow);margin-top:12px;overflow:hidden;height:clamp(760px,82vh,820px);
  background-image:radial-gradient(var(--bc-grid) 1.4px,transparent 1.4px);background-size:30px 30px;touch-action:none;}
.bc-viewport{position:absolute;top:0;left:0;width:2200px;height:1440px;transform-origin:0 0;will-change:transform;}
.bc-wires{position:absolute;inset:0;width:2200px;height:1440px;pointer-events:none;overflow:visible;}
.bc-wire{fill:none;stroke-width:3;stroke-linecap:round;stroke-dasharray:2 10;animation:bc-flow 1.1s linear infinite;transition:opacity .25s,stroke-width .2s;}
@keyframes bc-flow{to{stroke-dashoffset:-11}}
.bc-wire.off{stroke-dasharray:2 8;animation:none;opacity:.45;}
@media (prefers-reduced-motion:reduce){.bc-wire{animation:none}.bc-node .bc-bob{animation:none}.bc-hubbox{animation:none}.bc-ring{animation:none}}

.bc-node{position:absolute;transform:translate(-50%,-50%);z-index:2;cursor:pointer;text-align:center;width:148px;}
.bc-node .bc-bob{animation:bc-bob 5s ease-in-out infinite;}
.bc-node .bc-bubble{width:82px;height:82px;margin:0 auto;border-radius:27px;display:flex;align-items:center;justify-content:center;
  background:var(--bc-panel);border:3px solid var(--nc);box-shadow:var(--bc-shadow);transition:transform .2s,box-shadow .2s;position:relative;}
.bc-node .bc-bubble svg{width:37px;height:37px;stroke:var(--nc);stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round;}
.bc-node.off .bc-bubble{border-style:dashed;opacity:.72;}
.bc-node.off .bc-bubble svg{opacity:.85;}
.bc-node .bc-lab{margin-top:12px;font-size:16px;font-weight:700;color:var(--bc-ink);line-height:1.18;}
.bc-node .bc-meta{font-size:13px;font-weight:600;color:var(--bc-mut);margin-top:2px;}
.bc-node.off .bc-lab{color:var(--bc-mut);}
.bc-node.off .bc-meta{color:var(--bc-dim);}
.bc-node .bc-dot{position:absolute;top:-4px;right:-4px;width:16px;height:16px;border-radius:50%;background:var(--nc);border:3px solid var(--bc-panel);}
.bc-node .bc-dot::after{content:"";position:absolute;inset:-4px;border-radius:50%;border:2px solid var(--nc);opacity:.5;animation:bc-ping 2s ease-out infinite;}
@keyframes bc-ping{0%{transform:scale(.7);opacity:.6}100%{transform:scale(1.9);opacity:0}}
.bc-node.off .bc-dot{display:none;}
.bc-node:hover .bc-bubble{transform:translateY(-3px) scale(1.07);box-shadow:var(--bc-shadow-lg);}
@keyframes bc-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes bc-pop{0%{transform:scale(.4);opacity:0}70%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
.bc-node{animation:bc-pop .5s cubic-bezier(.3,1.3,.5,1) both;}

.bc-hub{position:absolute;z-index:3;cursor:pointer;transform:translate(-50%,-50%);}
.bc-hubbox{background:var(--bc-hubfill);border:4px solid var(--bc-hub);border-radius:34px;padding:26px 40px;text-align:center;
  box-shadow:var(--bc-shadow-lg);position:relative;animation:bc-breathe 5s ease-in-out infinite;}
@keyframes bc-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.035)}}
.bc-hubbox svg{width:42px;height:42px;stroke:var(--bc-hub);stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round;display:block;margin:0 auto 7px;}
.bc-hubbox .bc-t{font-size:24px;font-weight:800;letter-spacing:-.01em;color:var(--bc-ink);}
.bc-hubbox .bc-s{font-size:14px;font-weight:700;color:var(--bc-hub);margin-top:5px;display:flex;align-items:center;gap:6px;justify-content:center;}
.bc-hubbox .bc-chatcue{margin-top:12px;font-size:13px;font-weight:700;color:#fff;background:var(--bc-hub);border-radius:20px;padding:5px 15px;display:inline-block;}
.bc-ring{position:absolute;left:50%;top:50%;width:210px;height:210px;transform:translate(-50%,-50%);
  border:2px dashed var(--bc-hub);border-radius:50%;opacity:.22;z-index:2;animation:bc-spin 28s linear infinite;}
@keyframes bc-spin{to{transform:translate(-50%,-50%) rotate(360deg)}}

.bc-zoom{position:absolute;left:16px;bottom:16px;z-index:15;display:flex;flex-direction:column;gap:6px;}
.bc-zoom button{width:38px;height:38px;border-radius:12px;border:1px solid var(--bc-line);background:var(--bc-panel);color:var(--bc-ink);
  font:700 18px Nunito,system-ui,sans-serif;cursor:pointer;box-shadow:var(--bc-shadow);display:flex;align-items:center;justify-content:center;}
.bc-zoom button:hover{background:var(--bc-panel2);}
.bc-zoom button svg{width:17px;height:17px;stroke:var(--bc-ink);fill:none;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;}
.bc-hint{position:absolute;right:16px;bottom:16px;z-index:15;font-size:11px;font-weight:600;color:var(--bc-dim);
  background:var(--bc-panel);border:1px solid var(--bc-line);border-radius:20px;padding:5px 11px;box-shadow:var(--bc-shadow);}
.bc-sample{position:absolute;left:16px;top:16px;z-index:16;font-size:11px;font-weight:800;color:var(--bc-mut);
  background:var(--bc-panel);border:1px dashed var(--bc-line);border-radius:20px;padding:5px 12px;box-shadow:var(--bc-shadow);}
.bc-search{position:absolute;top:16px;left:50%;transform:translateX(-50%);z-index:17;width:min(340px,62%);
  font:600 14px Nunito,system-ui,sans-serif;color:var(--bc-ink);background:var(--bc-panel);
  border:1px solid var(--bc-line);border-radius:22px;padding:9px 16px;box-shadow:var(--bc-shadow);outline:none;}
.bc-search::placeholder{color:var(--bc-dim);font-weight:600;}
.bc-search:focus{border-color:var(--bc-hub);box-shadow:var(--bc-shadow-lg);}
.bc-node.bc-hit{z-index:7;}
.bc-node.bc-hit .bc-bubble{border-style:solid;transform:translateY(-3px) scale(1.14);
  box-shadow:0 0 0 3px var(--nc), 0 0 26px 5px var(--nc);}
.bc-node.bc-hit .bc-lab{color:var(--nc);}

.bc-tip{position:absolute;z-index:40;pointer-events:none;background:var(--bc-ink);color:var(--bc-bg);font-size:12px;font-weight:600;
  padding:8px 11px;border-radius:10px;opacity:0;transition:opacity .12s;transform:translate(-50%,-118%);white-space:nowrap;box-shadow:var(--bc-shadow-lg);}
.bc-tip small{opacity:.72;font-weight:600;}

.bc-detail{position:absolute;top:16px;right:16px;width:310px;max-width:calc(100% - 32px);background:var(--bc-panel);
  border:1px solid var(--bc-line);border-radius:18px;box-shadow:var(--bc-shadow-lg);z-index:50;
  opacity:0;transform:translateY(-8px) scale(.98);pointer-events:none;transition:.22s;max-height:calc(100% - 32px);overflow:auto;}
.bc-detail.open{opacity:1;transform:none;pointer-events:auto;}
.bc-detail .bc-dh{padding:16px 18px 12px;display:flex;gap:12px;align-items:center;border-bottom:1px solid var(--bc-line);position:sticky;top:0;background:var(--bc-panel);}
.bc-detail .bc-dh .bc-ic{width:44px;height:44px;border-radius:14px;flex:none;display:flex;align-items:center;justify-content:center;background:var(--bc-panel);border:2.5px solid var(--nc);}
.bc-detail .bc-dh .bc-ic svg{width:22px;height:22px;stroke:var(--nc);stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round;}
.bc-detail .bc-dh h4{margin:0;font-size:16px;font-weight:800;color:var(--bc-ink);}
.bc-detail .bc-dh p{margin:1px 0 0;font-size:12px;font-weight:600;color:var(--bc-mut);}
.bc-detail .bc-db{padding:14px 18px 18px;}
.bc-pill{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;padding:4px 11px;border-radius:20px;}
.bc-pill.ok{background:rgba(90,190,120,.18);color:#3f8a43;}
.bc-pill.warn{background:rgba(234,180,60,.2);color:#a97b12;}
.bc-pill.err{background:rgba(226,74,59,.16);color:#c0392b;}
.bc-pill.offp{background:rgba(174,182,189,.2);color:var(--bc-mut);}
[data-theme="dark"] .bc-root .bc-pill.ok{color:#7fce83;}
[data-theme="dark"] .bc-root .bc-pill.warn{color:#e6be5a;}
[data-theme="dark"] .bc-root .bc-pill.err{color:#f0897d;}
.bc-health{margin-top:13px;border-top:1px dashed var(--bc-line);padding-top:12px;}
.bc-health .bc-hl{font-size:11px;font-weight:800;color:var(--bc-mut);text-transform:uppercase;letter-spacing:.06em;margin:0 0 8px;}
.bc-health .bc-cov{font-size:12.5px;font-weight:700;color:var(--bc-ink);margin:9px 0 0;}
.bc-health .bc-gap{font-size:11.5px;font-weight:600;color:var(--bc-mut);margin:4px 0 0;}
.bc-live{margin:13px 0 0;font-size:12.5px;font-weight:600;color:var(--bc-ink);line-height:1.55;background:var(--bc-panel2);border-radius:12px;padding:11px 13px;}
.bc-live b{color:var(--nc);}
.bc-provlab{font-size:11px;font-weight:800;color:var(--bc-mut);text-transform:uppercase;letter-spacing:.06em;margin:14px 0 7px;}
.bc-chips{display:flex;flex-wrap:wrap;gap:6px;}
.bc-chip{font-size:11.5px;font-weight:700;padding:4px 10px;border-radius:8px;background:var(--bc-panel2);border:1px solid var(--bc-line);color:var(--bc-ink);}
.bc-kv{margin:12px 0 0;font-size:13px;}
.bc-kv .bc-r{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--bc-line);}
.bc-kv .bc-r:last-child{border:none;}
.bc-kv .bc-r span:first-child{color:var(--bc-mut);font-weight:600;}
.bc-kv .bc-r span:last-child{font-weight:800;font-variant-numeric:tabular-nums;color:var(--bc-ink);}
.bc-feed{margin-top:12px;border-top:1px dashed var(--bc-line);padding-top:11px;}
.bc-feed .bc-fr{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:600;color:var(--bc-mut);padding:3px 0;}
.bc-feed .bc-fr b{color:var(--nc);font-weight:800;}
.bc-feed .bc-fr .bc-d{width:7px;height:7px;border-radius:50%;background:var(--nc);flex:none;animation:bc-pulse 1.6s ease-in-out infinite;}
@keyframes bc-pulse{0%,100%{opacity:1}50%{opacity:.3}}
.bc-cta{margin-top:15px;width:100%;border:none;border-radius:12px;padding:11px;font:800 13px Nunito,system-ui,sans-serif;color:#fff;background:var(--nc);cursor:pointer;}
.bc-xb{margin-left:auto;background:none;border:none;color:var(--bc-mut);font-size:20px;cursor:pointer;line-height:1;align-self:flex-start;}

.bc-backdrop{position:absolute;inset:0;background:rgba(20,26,32,.34);backdrop-filter:blur(2px);z-index:70;opacity:0;pointer-events:none;transition:.25s;}
.bc-backdrop.open{opacity:1;pointer-events:auto;}
.bc-chat{position:absolute;right:18px;bottom:18px;top:18px;width:390px;max-width:calc(100% - 36px);background:var(--bc-panel);
  border:1px solid var(--bc-line);border-radius:22px;box-shadow:var(--bc-shadow-lg);z-index:80;display:flex;flex-direction:column;overflow:hidden;
  transform:translateY(16px) scale(.98);opacity:0;pointer-events:none;transition:.3s cubic-bezier(.3,1,.4,1);}
.bc-chat.open{transform:none;opacity:1;pointer-events:auto;}
.bc-chat .bc-ch{display:flex;align-items:center;gap:11px;padding:15px 16px;border-bottom:1px solid var(--bc-line);background:var(--bc-hubfill);}
.bc-chat .bc-ch .bc-av{width:40px;height:40px;border-radius:13px;background:var(--bc-panel);border:2.5px solid var(--bc-hub);display:flex;align-items:center;justify-content:center;flex:none;}
.bc-chat .bc-ch .bc-av svg{width:21px;height:21px;stroke:var(--bc-hub);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.bc-chat .bc-ch h4{margin:0;font-size:15px;font-weight:800;color:var(--bc-ink);}
.bc-chat .bc-ch p{margin:1px 0 0;font-size:11.5px;font-weight:700;color:var(--bc-hub);display:flex;align-items:center;gap:5px;}
.bc-chat .bc-ch .bc-live-dot{width:7px;height:7px;border-radius:50%;background:#4FBE73;}
.bc-msgs{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:12px;}
.bc-msg{max-width:84%;font-size:13.5px;font-weight:600;line-height:1.5;padding:11px 13px;border-radius:15px;}
.bc-msg.bot{align-self:flex-start;background:var(--bc-panel2);border:1px solid var(--bc-line);border-bottom-left-radius:5px;color:var(--bc-ink);}
.bc-msg.me{align-self:flex-end;background:var(--bc-hub);color:#fff;border-bottom-right-radius:5px;}
.bc-msg .bc-src{margin-top:8px;display:flex;flex-wrap:wrap;gap:5px;}
.bc-msg .bc-src span{font-size:10.5px;font-weight:800;background:var(--bc-panel);border:1px solid var(--bc-line);color:var(--bc-mut);border-radius:7px;padding:2px 7px;}
.bc-msg .bc-conf{margin-top:7px;font-size:10.5px;font-weight:800;color:var(--bc-mut);text-transform:uppercase;letter-spacing:.05em;}
.bc-sugg{display:flex;flex-wrap:wrap;gap:7px;padding:0 16px 8px;}
.bc-sugg button{font-size:12px;font-weight:700;color:var(--bc-hub);background:var(--bc-hubfill);border:1px solid var(--bc-line);
  border-radius:16px;padding:7px 12px;cursor:pointer;text-align:left;}
.bc-sugg button:hover{filter:brightness(.97);}
.bc-typing{align-self:flex-start;display:flex;gap:4px;padding:12px 14px;background:var(--bc-panel2);border:1px solid var(--bc-line);border-radius:15px;border-bottom-left-radius:5px;}
.bc-typing i{width:7px;height:7px;border-radius:50%;background:var(--bc-mut);animation:bc-blink 1.2s infinite;}
.bc-typing i:nth-child(2){animation-delay:.2s}.bc-typing i:nth-child(3){animation-delay:.4s}
@keyframes bc-blink{0%,60%,100%{opacity:.25}30%{opacity:1}}
.bc-cin{display:flex;gap:8px;padding:12px 14px;border-top:1px solid var(--bc-line);}
.bc-cin input{flex:1;border:1px solid var(--bc-line);background:var(--bc-panel2);border-radius:12px;padding:11px 13px;font:600 13.5px Nunito,system-ui,sans-serif;color:var(--bc-ink);outline:none;}
.bc-cin input:focus{border-color:var(--bc-hub);}
.bc-cin button{width:44px;border:none;border-radius:12px;background:var(--bc-hub);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;}
.bc-cin button svg{width:19px;height:19px;stroke:#fff;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.bc-loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:var(--bc-mut);font-size:13.5px;font-weight:700;z-index:20;pointer-events:none;}

/* ── connect-keys modal (reuses .bc-dh/.bc-db/.bc-cta/.bc-xb/.bc-live tokens) ── */
.bc-mbd{position:absolute;inset:0;background:rgba(20,26,32,.34);backdrop-filter:blur(2px);z-index:90;opacity:0;pointer-events:none;transition:.25s;}
.bc-mbd.open{opacity:1;pointer-events:auto;}
.bc-modal{position:absolute;left:50%;top:50%;transform:translate(-50%,-46%) scale(.98);width:368px;max-width:calc(100% - 32px);max-height:calc(100% - 32px);overflow:auto;
  background:var(--bc-panel);border:1px solid var(--bc-line);border-radius:20px;box-shadow:var(--bc-shadow-lg);z-index:100;opacity:0;pointer-events:none;transition:.22s;}
.bc-modal.open{opacity:1;pointer-events:auto;transform:translate(-50%,-50%) scale(1);}
.bc-form{display:flex;flex-direction:column;gap:11px;margin:14px 0 4px;}
.bc-fld{display:flex;flex-direction:column;gap:5px;}
.bc-fld .bc-fl{font-size:11.5px;font-weight:800;color:var(--bc-mut);text-transform:uppercase;letter-spacing:.05em;}
.bc-fld input{border:1px solid var(--bc-line);background:var(--bc-panel2);border-radius:10px;padding:10px 12px;font:600 13.5px Nunito,system-ui,sans-serif;color:var(--bc-ink);outline:none;}
.bc-fld input:focus{border-color:var(--nc,var(--bc-hub));}
.bc-mstat{font-size:13px;font-weight:600;color:var(--bc-mut);padding:8px 0;}
.bc-modal .bc-dh{padding:16px 18px 12px;display:flex;gap:12px;align-items:center;border-bottom:1px solid var(--bc-line);}
.bc-modal .bc-dh .bc-ic{width:44px;height:44px;border-radius:14px;flex:none;display:flex;align-items:center;justify-content:center;background:var(--bc-panel);border:2.5px solid var(--nc,var(--bc-hub));}
.bc-modal .bc-dh .bc-ic svg{width:22px;height:22px;stroke:var(--nc,var(--bc-hub));stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round;}
.bc-modal .bc-dh h4{margin:0;font-size:16px;font-weight:800;color:var(--bc-ink);}
.bc-modal .bc-dh p{margin:1px 0 0;font-size:12px;font-weight:600;color:var(--bc-mut);}
.bc-modal .bc-db{padding:14px 18px 18px;}
.bc-cta:disabled{opacity:.6;cursor:default;}
`

// Load Nunito once (mirrors the prototype's Google Fonts link) — guarded, no external icon libs.
function ensureFont() {
  if (typeof document === 'undefined' || document.getElementById('bc-nunito-font')) return
  const l = document.createElement('link')
  l.id = 'bc-nunito-font'
  l.rel = 'stylesheet'
  l.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap'
  document.head.appendChild(l)
}

export default function BrainConstellation({ highlight = [] }) {
  const navigate = useNavigate()
  const navRef = useRef(navigate)
  navRef.current = navigate                 // keep the effect's closure pointed at the live navigate
  const rootRef = useRef(null)
  const highlightRef = useRef(new Set())
  const applyHighlightRef = useRef(null)   // set by the effect once the board is built
  const [counts, setCounts] = useState({ on: 0, total: 0 })
  const [sample, setSample] = useState(false)
  const [loading, setLoading] = useState(true)

  // Keep the imperative board in sync with the highlight prop without rebuilding it.
  useEffect(() => {
    highlightRef.current = new Set((highlight || []).filter(Boolean))
    if (applyHighlightRef.current) applyHighlightRef.current()
  }, [highlight])

  useEffect(() => {
    ensureFont()
    const root = rootRef.current
    if (!root) return
    let alive = true
    const disposers = []
    const on = (el, ev, fn, opts) => { el.addEventListener(ev, fn, opts); disposers.push(() => el.removeEventListener(ev, fn, opts)) }

    // scoped query helpers
    const $ = (sel) => root.querySelector(sel)
    const $$ = (sel) => [...root.querySelectorAll(sel)]

    const board = $('.bc-board'), vp = $('.bc-viewport'), wiresEl = $('.bc-wires')
    const tip = $('.bc-tip'), detail = $('.bc-detail'), backdrop = $('.bc-backdrop'), chat = $('.bc-chat')
    const modalBd = $('.bc-mbd'), modal = $('.bc-modal')
    const VW = 2200, VH = 1440, CX = 1100, CY = 720
    const T = { s: 1, tx: 0, ty: 0 }
    let SOURCES = []
    let labelById = {}

    // ── zoom / pan ──────────────────────────────────────────────────────────
    const apply = () => { vp.style.transform = `translate(${T.tx}px,${T.ty}px) scale(${T.s})` }
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
    const fit = () => {
      const bw = board.clientWidth, bh = board.clientHeight
      if (!bw || !bh || !SOURCES.length) return
      const xs = SOURCES.map((s) => s.vx), ys = SOURCES.map((s) => s.vy)
      const pad = 170
      const minX = Math.min(...xs, CX) - pad, maxX = Math.max(...xs, CX) + pad
      const minY = Math.min(...ys, CY) - pad - 30, maxY = Math.max(...ys, CY) + pad + 30
      const cw = maxX - minX, ch = maxY - minY
      T.s = clamp(Math.min(bw / cw, bh / ch), 0.4, 2.6)
      T.tx = (bw - cw * T.s) / 2 - minX * T.s
      T.ty = (bh - ch * T.s) / 2 - minY * T.s
      apply()
    }
    const zoomAt = (mx, my, factor) => {
      const ns = clamp(T.s * factor, 0.4, 2.6)
      T.tx = mx - (mx - T.tx) * (ns / T.s)
      T.ty = my - (my - T.ty) * (ns / T.s)
      T.s = ns; apply()
    }

    // ── highlight / dim ─────────────────────────────────────────────────────
    const restore = () => {
      const set = highlightRef.current
      if (set && set.size) {
        $$('.bc-node').forEach((n) => { n.style.opacity = set.has(n.dataset.id) ? '1' : '.24' })
        $$('.bc-wire').forEach((w) => {
          const s = SOURCES[+w.dataset.i]
          const lit = s && set.has(s.id)
          w.style.opacity = lit ? '1' : '.1'
          w.style.strokeWidth = lit ? '3.6' : ''
        })
      } else {
        $$('.bc-wire').forEach((w) => { w.style.opacity = ''; w.style.strokeWidth = '' })
        $$('.bc-node').forEach((n) => { n.style.opacity = '' })
      }
    }
    applyHighlightRef.current = restore

    const dimAll = () => {
      $$('.bc-wire').forEach((w) => { w.style.opacity = '.1' })
      $$('.bc-node').forEach((n) => { n.style.opacity = '.26' })
    }
    const hover = (s, n) => {
      const i = SOURCES.indexOf(s)
      dimAll(); n.style.opacity = '1'
      const w = $(`.bc-wire[data-i="${i}"]`)
      if (w) { w.style.opacity = '1'; w.style.strokeWidth = '3.6' }
      const sx = T.tx + s.vx * T.s, sy = T.ty + s.vy * T.s
      tip.innerHTML = `${esc(s.label)} · ${esc(s.cat)}<br><small>${s.on ? s.metrics.toLocaleString() + ' data points flowing in' : 'not connected yet'}</small>`
      tip.style.left = sx + 'px'; tip.style.top = (sy - 46 * T.s) + 'px'; tip.style.opacity = '1'
    }

    // ── detail card ─────────────────────────────────────────────────────────
    const closeD = () => detail.classList.remove('open')
    const openSource = (s) => {
      closeChat(); closeModal()
      detail.style.setProperty('--nc', s.on ? s.c : '#7C8794')
      const feed = s.on
        ? `<div class="bc-feed">${s.sends.slice(0, 3).map((x) => `<div class="bc-fr"><span class="bc-d"></span>streaming <b>${esc(x)}</b> into the Brain now</div>`).join('')}</div>`
        : ''
      // health block — connected sources only, and only when health fields are present
      const hasCov = s.coveragePct != null && !Number.isNaN(s.coveragePct)
      const hasGaps = Array.isArray(s.gaps) && s.gaps.length
      const health = (s.on && (s.health_status || hasCov || hasGaps))
        ? (() => {
            const [pcls, plbl] = HEALTH_PILL[s.health_status] || (s.health_status ? ['offp', s.health_status] : [null, null])
            const pill = plbl ? `<span class="bc-pill ${pcls}">● ${esc(plbl)}</span>` : ''
            const cov = hasCov ? `<p class="bc-cov">Field coverage ${Math.round(s.coveragePct)}%</p>` : ''
            const gaps = hasGaps ? s.gaps.map((g) => `<p class="bc-gap">missing: ${esc(g)}</p>`).join('') : ''
            return `<div class="bc-health"><p class="bc-hl">Connection health</p>${pill}${cov}${gaps}</div>`
          })()
        : ''
      detail.innerHTML = `<div class="bc-dh"><div class="bc-ic">${ic(s.icon)}</div><div><h4>${esc(s.label)}</h4><p>${esc(s.cat)} · ${s.on ? 'connected' : 'available'}</p></div><button class="bc-xb" data-close-d aria-label="Close">×</button></div>
        <div class="bc-db"><span class="bc-pill ${s.on ? 'ok' : 'offp'}">${s.on ? '● Live' : '○ Not connected'}</span>
          <p class="bc-live">${s.on ? 'Right now:' : 'If connected:'} <b>${esc(s.activity)}</b>.</p>
          <p class="bc-provlab">Data it sends the brain</p><div class="bc-chips">${s.sends.map((x) => `<span class="bc-chip">${esc(x)}</span>`).join('')}</div>
          <div class="bc-kv"><div class="bc-r"><span>Data points</span><span>${s.on ? s.metrics.toLocaleString() : '—'}</span></div>
          <div class="bc-r"><span>Sync frequency</span><span>${esc(s.freq)}</span></div>
          <div class="bc-r"><span>Last pull</span><span>${s.on ? esc(relTime(s.last_sync_at)) : '—'}</span></div></div>
          ${health}
          ${feed}
          ${s.on
            ? `<button class="bc-cta" data-dash>View ${esc(s.label)} in dashboards</button>`
            : `<button class="bc-cta" data-connect>Connect ${esc(s.label)}</button>`}</div>`
      detail.querySelectorAll('[data-close-d]').forEach((b) => b.addEventListener('click', closeD))
      detail.querySelectorAll('[data-connect]').forEach((b) => b.addEventListener('click', () => openConnect(s)))
      // "View in dashboards" → route SEO sources to the SEO Intelligence surface, everything
      // else to Analytics (both read this source's live data). Uses the router, no reload.
      detail.querySelectorAll('[data-dash]').forEach((b) => b.addEventListener('click', () => {
        const path = /seo/i.test(s.cat || '') ? '/seo-intelligence' : '/analytics'
        closeD(); if (navRef.current) navRef.current(path)
      }))
      detail.classList.add('open')
    }

    // ── connect-keys modal: let the USER add their own credentials per source ──
    const closeModal = () => { modal.classList.remove('open'); modalBd.classList.remove('open') }
    on(modalBd, 'click', closeModal)
    // Escape always closes the connect modal + the detail card — a reliable way out.
    on(document, 'keydown', (e) => { if (e.key === 'Escape') { closeModal(); closeD() } })
    // Refetch connectivity and rebuild so a just-connected node flips to "connected".
    const refetchConnectivity = async () => {
      try {
        const r = await axios.get(`${API}/connectivity`)
        const list = Array.isArray(r.data?.connectors) ? r.data.connectors : []
        if (!alive) return
        if (list.length) { SOURCES = list.map(toNode); setSample(false) }
        labelById = Object.fromEntries(SOURCES.map((s) => [s.id, s.label]))
        setCounts({ on: SOURCES.filter((s) => s.on).length, total: SOURCES.length })
        vp.querySelectorAll('.bc-node,.bc-hub').forEach((el) => el.remove())
        build()
      } catch { /* keep current board on refetch failure */ }
    }
    const openConnect = async (s) => {
      closeD()
      modal.style.setProperty('--nc', s.c || 'var(--bc-hub)')
      modal.innerHTML = `<div class="bc-dh"><div class="bc-ic">${ic(s.icon)}</div><div><h4>Connect ${esc(s.label)}</h4><p>${esc(s.cat)}</p></div><button class="bc-xb" data-close-m aria-label="Close">×</button></div><div class="bc-db"><div class="bc-mstat">Loading connection options…</div></div>`
      modal.querySelectorAll('[data-close-m]').forEach((b) => b.addEventListener('click', closeModal))
      modalBd.classList.add('open'); modal.classList.add('open')
      try {
        const r = await axios.get(`${API}/connectors/${encodeURIComponent(s.id)}/fields`)
        if (!alive) return
        const d = r.data || {}
        const body = modal.querySelector('.bc-db')
        if (!body) return
        if (d.auth === 'oauth') {
          const url = d.connectUrl || d.connect_url || d.url || ''
          body.innerHTML = `<p class="bc-live">Connect <b>${esc(s.label)}</b> securely with OAuth. You'll be sent to ${esc(s.label)} to approve access — your credentials are never seen or stored here.</p><button class="bc-cta" data-oauth type="button">Connect with OAuth</button>`
          body.querySelector('[data-oauth]').addEventListener('click', () => {
            if (url) window.location.assign(url)
            else toast.error(`No OAuth link was returned for ${s.label}.`)
          })
        } else {
          const fields = Array.isArray(d.fields) ? d.fields : []
          if (!fields.length) {
            body.innerHTML = `<p class="bc-live">No connection fields are available for <b>${esc(s.label)}</b> yet.</p>`
            return
          }
          body.innerHTML = `<p class="bc-live">Enter your <b>${esc(s.label)}</b> API credentials. They're sent straight to your account's secure vault — we never see or keep them.</p>
            <div class="bc-form">${fields.map((f) => {
              const key = f.key || f.name || f.id || ''
              const lbl = f.label || key
              const isSecret = f.secret === true || f.type === 'password' || /secret|token|key|password|pass/i.test(key)
              const req = f.required !== false
              return `<label class="bc-fld"><span class="bc-fl">${esc(lbl)}${req ? ' *' : ''}</span><input data-fkey="${esc(key)}" type="${isSecret ? 'password' : 'text'}" placeholder="${esc(f.placeholder || '')}" autocomplete="off"${req ? ' data-required="1"' : ''}></label>`
            }).join('')}</div>
            <button class="bc-cta" data-connect-submit type="button">Connect ${esc(s.label)}</button>`
          const submit = body.querySelector('[data-connect-submit]')
          submit.addEventListener('click', async () => {
            const inputs = [...body.querySelectorAll('input[data-fkey]')]
            const credentials = {}
            let missing = false
            inputs.forEach((inp) => {
              const v = inp.value.trim()
              if (inp.dataset.required && !v) missing = true
              if (v) credentials[inp.dataset.fkey] = v
            })
            if (missing) { toast.error('Please fill in all required fields.'); return }
            submit.disabled = true; submit.textContent = 'Verifying…'
            try {
              // The server validates the key by actually calling the provider, so a wrong
              // key throws here (caught below). On success it tells us whether it verified
              // live data or just saved the key (no live puller yet) — report honestly.
              const resp = await axios.post(`${API}/connectors/${encodeURIComponent(s.id)}/connect`, { credentials })
              const d = resp.data || {}
              if (d.verified) toast.success(`${s.label} connected & verified ✓`)
              else toast.success(d.note || `${s.label} saved`)
              closeModal()
              await refetchConnectivity()
            } catch (err) {
              const m = err?.response?.data?.error || `Couldn't connect ${s.label}. Check your keys and try again.`
              toast.error(m)
              submit.disabled = false; submit.textContent = `Connect ${s.label}`
            }
          })
        }
      } catch (err) {
        if (!alive) return
        const m = err?.response?.data?.error || `Couldn't load connection options for ${s.label} right now.`
        const body = modal.querySelector('.bc-db')
        if (body) body.innerHTML = `<p class="bc-live">${esc(m)}</p>`
        toast.error(m)
      }
    }

    // ── brain chat (wired to POST /ask) ─────────────────────────────────────
    const closeChat = () => { backdrop.classList.remove('open'); chat.classList.remove('open') }
    const match = (v) => {
      v = v.toLowerCase()
      return SUGGESTIONS.find((q) => /(waste|wasting|spend)/.test(v) && /waste|wasting|spend/.test(q.toLowerCase()))
        || SUGGESTIONS.find((q) => /(test|experiment|week)/.test(v) && /test/.test(q.toLowerCase()))
        || SUGGESTIONS[0]
    }
    const ask = async (q) => {
      const msgs = $('.bc-msgs'), sg = $('.bc-sugg')
      if (!q || !msgs) return
      if (sg) sg.style.display = 'none'
      msgs.insertAdjacentHTML('beforeend', `<div class="bc-msg me">${esc(q)}</div>`)
      const typ = document.createElement('div'); typ.className = 'bc-typing'; typ.innerHTML = '<i></i><i></i><i></i>'
      msgs.appendChild(typ); msgs.scrollTop = msgs.scrollHeight
      const input = $('.bc-cin input'); if (input) input.value = ''
      try {
        const r = await axios.post(`${API}/ask`, { question: q })
        const d = r.data || {}
        typ.remove()
        const answer = d.answer || d.summary || 'I could not find enough in your connected data to answer that yet.'
        const srcIds = (Array.isArray(d.sources) ? d.sources : [])
          .map((s) => (typeof s === 'string' ? s : (s?.provider || s?.label || ''))).filter(Boolean)
        const srcHtml = srcIds.length
          ? `<div class="bc-src">${srcIds.map((s) => `<span>◆ ${esc(labelById[s] || s)}</span>`).join('')}</div>` : ''
        let pct = null
        if (typeof d.confidence === 'number') pct = d.confidence <= 1 ? Math.round(d.confidence * 100) : Math.round(d.confidence)
        const confHtml = pct != null ? `<div class="bc-conf">confidence: ${confWord(pct)} · ${pct}%</div>` : ''
        msgs.insertAdjacentHTML('beforeend', `<div class="bc-msg bot">${esc(answer)}${srcHtml}${confHtml}</div>`)
      } catch (err) {
        typ.remove()
        const m = err?.response?.data?.error || 'I hit a snag reaching your data just now — give me a moment and try again.'
        msgs.insertAdjacentHTML('beforeend', `<div class="bc-msg bot">${esc(m)}</div>`)
      }
      msgs.scrollTop = msgs.scrollHeight
    }
    const openChat = () => {
      closeD(); closeModal(); backdrop.classList.add('open')
      const liveN = SOURCES.filter((s) => s.on).length
      chat.innerHTML = `<div class="bc-ch"><div class="bc-av">${ic('cpu')}</div><div><h4>Marketing Brain</h4><p><span class="bc-live-dot"></span>online · reasoning over ${liveN} source${liveN === 1 ? '' : 's'}</p></div><button class="bc-xb" data-close-chat aria-label="Close">×</button></div>
        <div class="bc-msgs"><div class="bc-msg bot">Hi — I'm your Marketing Brain. I read every connected source and answer with the evidence behind it. Ask me anything, or start here:</div></div>
        <div class="bc-sugg">${SUGGESTIONS.map((q) => `<button type="button">${esc(q)}</button>`).join('')}</div>
        <div class="bc-cin"><input placeholder="Ask about revenue, spend, funnel…" autocomplete="off"><button type="button" data-send aria-label="Send">${ic('send')}</button></div>`
      chat.classList.add('open')
      chat.querySelectorAll('[data-close-chat]').forEach((b) => b.addEventListener('click', closeChat))
      chat.querySelectorAll('.bc-sugg button').forEach((b) => b.addEventListener('click', () => ask(b.textContent)))
      const input = chat.querySelector('.bc-cin input')
      chat.querySelector('[data-send]').addEventListener('click', () => { const v = input.value.trim(); if (v) ask(v) })
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { const v = e.target.value.trim(); if (v) ask(v) } })
      setTimeout(() => { if (alive && input) input.focus() }, 300)
    }
    on(backdrop, 'click', closeChat)

    // ── wires ───────────────────────────────────────────────────────────────
    const drawWires = () => {
      let defs = '<defs>', paths = ''
      SOURCES.forEach((s, i) => {
        const mx = (s.vx + CX) / 2 + (s.vy < CY ? 30 : -30), my = (s.vy + CY) / 2 + (s.vx < CX ? 26 : -26)
        const d = `M${s.vx.toFixed(1)},${s.vy.toFixed(1)} Q${mx.toFixed(1)},${my.toFixed(1)} ${CX},${CY}`
        const col = s.on ? s.c : 'var(--bc-line)'
        defs += `<marker id="bcar${i}" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6" fill="none" stroke="${col}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></marker>`
        paths += `<path class="bc-wire${s.on ? '' : ' off'}" data-i="${i}" d="${d}" stroke="${col}" marker-end="url(#bcar${i})"/>`
      })
      wiresEl.innerHTML = defs + '</defs>' + paths
    }

    // ── build the constellation from resolved SOURCES ───────────────────────
    const build = () => {
      const onArr = SOURCES.filter((s) => s.on), offArr = SOURCES.filter((s) => !s.on)
      const ring = (arr, rB, rJ, ph) => arr.forEach((s, i) => {
        const a = ph + i * (2 * Math.PI / Math.max(1, arr.length))
        const r = rB + (i % 2 ? rJ : -rJ) + 46 * Math.sin(i * 1.4)
        s.vx = CX + Math.cos(a) * r * 1.36
        s.vy = CY + Math.sin(a) * r
      })
      ring(onArr, 460, 66, -0.35)
      ring(offArr, 730, 62, -0.35 + Math.PI / Math.max(1, offArr.length))

      // hub
      const hub = document.createElement('div')
      hub.className = 'bc-hub'; hub.style.left = CX + 'px'; hub.style.top = CY + 'px'
      hub.innerHTML = `<div class="bc-ring"></div><div class="bc-hubbox">${ic('cpu')}<div class="bc-t">Marketing Brain</div><div class="bc-s"><span style="width:7px;height:7px;border-radius:50%;background:#4FBE73;display:inline-block"></span>reasoning over ${onArr.length} source${onArr.length === 1 ? '' : 's'}</div><span class="bc-chatcue">💬 tap to ask</span></div>`
      vp.appendChild(hub)
      hub.addEventListener('click', (e) => { e.stopPropagation(); openChat() })

      // nodes
      SOURCES.forEach((s, i) => {
        const n = document.createElement('div')
        n.className = 'bc-node' + (s.on ? '' : ' off')
        n.dataset.id = s.id
        n.style.setProperty('--nc', s.c)
        n.style.left = s.vx + 'px'; n.style.top = s.vy + 'px'; n.style.animationDelay = (i * 0.06) + 's'
        n.innerHTML = `<div class="bc-bob" style="animation-delay:${i * 0.5}s"><div class="bc-bubble">${s.on ? '<span class="bc-dot"></span>' : ''}${ic(s.icon)}</div><div class="bc-lab">${esc(s.label)}</div><div class="bc-meta">${s.on ? s.metrics.toLocaleString() + ' pts' : 'connect'}</div></div>`
        vp.appendChild(n)
        n.addEventListener('mouseenter', () => hover(s, n))
        n.addEventListener('mouseleave', () => { tip.style.opacity = '0'; restore() })
        n.addEventListener('click', (e) => { e.stopPropagation(); openSource(s) })
      })

      drawWires()
      fit()
      restore()
    }

    // ── pan + zoom listeners (attached once) ────────────────────────────────
    on(board, 'wheel', (e) => {
      e.preventDefault()
      const r = board.getBoundingClientRect()
      zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.12 : 1 / 1.12)
    }, { passive: false })
    on($('.bc-zin'), 'click', () => zoomAt(board.clientWidth / 2, board.clientHeight / 2, 1.2))
    on($('.bc-zout'), 'click', () => zoomAt(board.clientWidth / 2, board.clientHeight / 2, 1 / 1.2))
    on($('.bc-zfit'), 'click', fit)

    // ── search → highlight ──────────────────────────────────────────────────
    // Type a connector name; matches glow, everything else dims, and the first match
    // is panned to centre so you never hunt across 50 nodes. Empty query restores all.
    const searchEl = $('.bc-search')
    const clearHits = () => $$('.bc-node').forEach((n) => n.classList.remove('bc-hit'))
    const runSearch = (raw) => {
      const q = (raw || '').trim().toLowerCase()
      if (!q) { clearHits(); restore(); return }
      const matches = SOURCES.filter((s) => (s.label || '').toLowerCase().includes(q) || (s.cat || '').toLowerCase().includes(q))
      const ids = new Set(matches.map((s) => s.id))
      $$('.bc-node').forEach((n) => {
        const hit = ids.has(n.dataset.id)
        n.style.opacity = hit ? '1' : '.1'
        n.classList.toggle('bc-hit', hit)
      })
      $$('.bc-wire').forEach((w) => { const s = SOURCES[+w.dataset.i]; w.style.opacity = s && ids.has(s.id) ? '.9' : '.05' })
      if (matches.length) {
        const s = matches[0]
        T.tx = board.clientWidth / 2 - s.vx * T.s
        T.ty = board.clientHeight / 2 - s.vy * T.s
        apply()
      }
    }
    if (searchEl) {
      on(searchEl, 'input', (e) => runSearch(e.target.value))
      on(searchEl, 'keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); runSearch(e.target.value) }
        if (e.key === 'Escape') { searchEl.value = ''; clearHits(); restore() }
      })
    }
    let drag = null
    on(board, 'pointerdown', (e) => {
      // Never start a pan (which captures the pointer and eats the click) when the press
      // begins on an interactive overlay — the connect modal + its backdrop MUST be here,
      // or pressing any modal button steals the click and nothing fires (submit, X, OAuth).
      if (e.target.closest('.bc-node,.bc-hub,.bc-detail,.bc-chat,.bc-zoom,.bc-backdrop,.bc-modal,.bc-mbd,.bc-tip')) return
      drag = { x: e.clientX, y: e.clientY, tx: T.tx, ty: T.ty }
      try { board.setPointerCapture(e.pointerId) } catch { /* noop */ }
      board.style.cursor = 'grabbing'
    })
    on(board, 'pointermove', (e) => {
      if (!drag) return
      T.tx = drag.tx + (e.clientX - drag.x); T.ty = drag.ty + (e.clientY - drag.y)
      apply(); tip.style.opacity = '0'
    })
    on(board, 'pointerup', () => { drag = null; board.style.cursor = '' })
    on(board, 'pointerleave', () => { drag = null; board.style.cursor = '' })
    on(window, 'resize', fit)

    // ── data → build ────────────────────────────────────────────────────────
    axios.get(`${API}/connectivity`)
      .then((r) => {
        if (!alive) return
        const list = Array.isArray(r.data?.connectors) ? r.data.connectors : []
        if (list.length) {
          SOURCES = list.map(toNode)
          setSample(false)
        } else {
          SOURCES = SAMPLE_SOURCES.map((s) => ({ ...s }))
          setSample(true)
        }
      })
      .catch(() => {
        if (!alive) return
        SOURCES = SAMPLE_SOURCES.map((s) => ({ ...s }))
        setSample(true)
      })
      .finally(() => {
        if (!alive) return
        labelById = Object.fromEntries(SOURCES.map((s) => [s.id, s.label]))
        setCounts({ on: SOURCES.filter((s) => s.on).length, total: SOURCES.length })
        setLoading(false)
        build()
      })

    return () => {
      alive = false
      applyHighlightRef.current = null
      disposers.forEach((d) => d())
      if (vp) vp.querySelectorAll('.bc-node,.bc-hub').forEach((el) => el.remove())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="bc-root" ref={rootRef}>
      <style>{CSS}</style>
      <div className="bc-head">
        <div>
          <h3>How your Marketing Brain is wired</h3>
          <p>Scroll to zoom, drag to pan. Tap a source for what it's sending — or tap the Brain to talk to it.</p>
        </div>
        <div className="bc-count"><b>{counts.on}</b>of {counts.total} sources live</div>
      </div>
      <div className="bc-board">
        <div className="bc-viewport">
          <svg className="bc-wires" viewBox="0 0 2200 1440" />
        </div>
        <input className="bc-search" type="search" placeholder="Search connectors…  (Enter)" aria-label="Search connectors" />
        <div className="bc-zoom">
          <button className="bc-zin" type="button" aria-label="Zoom in">+</button>
          <button className="bc-zout" type="button" aria-label="Zoom out">−</button>
          <button className="bc-zfit" type="button" aria-label="Fit">
            <svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" /></svg>
          </button>
        </div>
        <div className="bc-hint">scroll to zoom · drag to pan</div>
        {sample && <div className="bc-sample">sample — connect sources for live data</div>}
        {loading && <div className="bc-loading">Waking the Brain…</div>}
        <div className="bc-tip" />
        <div className="bc-detail" />
        <div className="bc-backdrop" />
        <div className="bc-chat" />
        <div className="bc-mbd" />
        <div className="bc-modal" />
      </div>
    </div>
  )
}
